import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;
// Check for zombie WebSocket connections every 30 seconds
const LIVENESS_CHECK_INTERVAL = 30000;
// If no message received for 2 minutes, treat connection as dead
const LIVENESS_TIMEOUT = 120000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.lastReceivedAt = Date.now();
    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          this.lastReceivedAt = Date.now();
        },
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.livenessTimer = null;
    this.isAValidEvent = () => true;
    this.triggerPresenceInterval = () => {
      setTimeout(() => {
        this.subscription.updatePresence();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
    this.startLivenessCheck();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    const isReconnected =
      BaseActionCableConnector.isDisconnected && isConnectionActive;
    if (isReconnected) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  startLivenessCheck = () => {
    this.clearLivenessCheck();
    this.livenessTimer = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastReceivedAt;
      const isConnectionOpen = this.consumer.connection.isOpen();

      if (timeSinceLastMessage > LIVENESS_TIMEOUT && !isConnectionOpen) {
        // Zombie connection detected — force disconnect and reconnect
        if (!BaseActionCableConnector.isDisconnected) {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
        }
        this.consumer.connection.reopen();
        this.initReconnectTimer();
      } else if (
        timeSinceLastMessage > LIVENESS_TIMEOUT &&
        isConnectionOpen
      ) {
        // Connection appears open but no data received — force reopen
        // to clear any stale server-side state
        this.consumer.connection.reopen();
      }
    }, LIVENESS_CHECK_INTERVAL);
  };

  clearLivenessCheck = () => {
    if (this.livenessTimer) {
      clearInterval(this.livenessTimer);
      this.livenessTimer = null;
    }
  };

  disconnect() {
    this.clearLivenessCheck();
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    this.lastReceivedAt = Date.now();
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
